import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $connectionStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { IntegrationStep, type IntegrationResponse } from '@shared/api';
import { Button, Spinner, Text } from '@shared/widgets';
import xeroImg from '@assets/icons/integrations/xero-logo.svg?url';

interface IntegrationCardProps {
  integration: IntegrationResponse;
}

export const IntegrationCard = (props: IntegrationCardProps) => {
  const { navigate } = useRouteHook();

  const connectionStore = useUnit($connectionStore.store);

  const { t } = useTranslation('integrations');

  return (
    <div
      className={cn(
        'flex flex-col gap-5 bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
      )}
    >
      <div className='flex gap-5'>
        <div className='flex h-24 w-24 flex-none items-center justify-center bg-ice dark:bg-eerie-black'>
          <img className='flex-none object-contain' src={xeroImg} alt='Xero' />
        </div>
        <div className='flex flex-col gap-1'>
          <Text weight='medium' size='xl'>
            {props.integration.name}
          </Text>
          <Text size='sm' color='secondary'>
            {t(`card.description.${props.integration.name}`)}
          </Text>
        </div>
      </div>
      <div className='flex items-center gap-5'>
        {props.integration.connected ? (
          <>
            <Button fullWidth color='outlined' onClick={() => navigate(RoutePaths.integration.onNavigate(props.integration.name))}>
              {t('card.buttons.configure')}
            </Button>
            <Button
              fullWidth
              color='outlined'
              disabled={connectionStore.loading || connectionStore.data === IntegrationStep.Disconnect}
              onClick={() => $connectionStore.events.disconnect(props.integration.name)}
            >
              {t('card.buttons.disconnect')}
              {(connectionStore.loading || connectionStore.data === IntegrationStep.Disconnect) && <Spinner embedded />}
            </Button>
          </>
        ) : (
          <Button
            fullWidth
            color='primary'
            disabled={connectionStore.loading || connectionStore.data === IntegrationStep.Connect}
            onClick={() => $connectionStore.events.connect(props.integration.name)}
          >
            {t('card.buttons.integrate')}
            {(connectionStore.loading || connectionStore.data === IntegrationStep.Connect) && <Spinner embedded />}
          </Button>
        )}
      </div>
    </div>
  );
};
